import { api } from '@/api/client';
import type { StaffSession } from '@/api/types';

import { withRefreshLock } from './crossTabLock';
import { sessionStore } from './session';

/**
 * Exchange the refresh token, at most once at a time — in this tab, and across
 * every other tab on the origin.
 *
 * Single-flight at module scope rather than inside a component, because the two
 * callers race in exactly the situation that hurts most. Refresh tokens rotate
 * and the server treats a reused one as theft — it revokes the whole family —
 * so two concurrent exchanges of the same token do not merely waste a round
 * trip, they sign the user out of a working session.
 *
 * The two callers: AuthProvider restoring a session on boot, and the HTTP
 * client retrying a 401. StrictMode's double-invoked effect is the third, and
 * the one that would have made this a bug reproducible on every dev reload.
 *
 * The fourth caller is another tab, and it is the one that fired the theft
 * alarm in real use: the token is shared through localStorage, so a second tab
 * rotating it leaves the first holding a token the server has already retired.
 * The cross-tab lock makes the rotations queue. One tab spends the live token
 * at a time and the next to be granted the lock reads what that rotation wrote,
 * so the server never sees the same token twice.
 *
 * A waiter cannot simply adopt the winner's session: the access token is
 * memory-only by design and never crosses a tab boundary. So it spends its turn
 * on its own exchange of the now-current token, which costs one round trip per
 * tab and is the price of not putting an access token in shared storage.
 */

let inFlight: Promise<StaffSession | null> | null = null;

async function exchange(): Promise<StaffSession | null> {
  // Read inside the lock, never before waiting for it. Whoever held it may have
  // rotated the token this tab was carrying, and not spending that one is the
  // entire point of having waited.
  const refreshToken = sessionStore.getRefreshToken();
  if (refreshToken === null) return null;

  try {
    const session = await api.post<StaffSession>(
      '/staff/auth/refresh',
      { refreshToken },
      { authenticated: false },
    );
    sessionStore.set(session);
    return session;
  } catch {
    // A refused refresh is the end of the session, not a retryable blip: the
    // token has been rotated away, revoked, or the account has been switched
    // off. Null, and the caller decides whether that means /login.
    return null;
  }
}

async function exchangeUnderLock(): Promise<StaffSession | null> {
  try {
    return await withRefreshLock(exchange);
  } catch {
    // The lock is coordination, not a credential. A browser that exposes
    // navigator.locks and then refuses the request must still be able to
    // restore a session; one unsynchronised refresh beats no session at all.
    return exchange();
  }
}

export function refreshStaffSession(): Promise<StaffSession | null> {
  inFlight ??= exchangeUnderLock().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
